import _ from "lodash";
import React, {
  PureComponent,
  useState,
  memo,
  useRef,
  cloneElement,
  useEffect,
} from "react";

// components
import TextAreaInput from "UIComponents/TextAreaInput";
import I18nHOC from "UIComponents/I18nHOC";

// design system
import { Button, Dialog, IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  DeleteOutlined,
  AssessmentApplicableOutlined,
  DotsSixVerticalOutlined,
  AddColumnOutlined,
} from "@toddle-edu/ds-icons";
import { elevations } from "@toddle-edu/ds-theme";

// modules
import {
  createRubricIndicator,
  writeRubricElementLocal,
  deleteRubricIndicator,
  createRubricCriteria,
  deleteRubricCriteria,
} from "Rubric/modules/RubricModule";
import { updateField } from "modules/FieldUpdateThunks";
import { reorderAssessmentToolItem } from "modules/AssessmentTool/AssessmentToolModule";

// third party
import { connect } from "react-redux";
import classNames from "classnames";
import { compose } from "lib/apolloClient";

import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensors,
  useSensor,
  useDraggable,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";

// scss
import classes from "./RubricTableWithReordering.scss";

// constants
import { RUBRIC } from "Constants/stringConstants";
import { colors, fontStyle } from "Constants";

// utils
import { moveElementInArray, useIntersectionObserver } from "Utils";
import {
  getProjectionLineData,
  sortAssessmentTool,
} from "ClassRoom/modules/utils";
import { getItemBeforeAndAfter } from "Utils/Common";
import { saveAssessmentToolUpdateStatusInRedux } from "Assessments/modules/AssessmentModule";
import { Popover } from "@toddle-edu/react-tiny-popover";
import { isEmpty } from "lodash";
import { useI18n } from "Hooks";

const textAreaStyles = {
  cell: {
    border: "none",
    marginTop: 0,
    flexShrink: 0,
    lineHeight: 1.57,
    width: "100%",
    fontSize: "1.4rem",
    padding: "10px",
    overflow: "hidden",
    minHeight: "100%",
    ...fontStyle.medium,
    height: "unset",
    color: colors.textDefault,
  },
  headerCell: {
    fontSize: "1.4rem",
    padding: "10px",
    height: "unset",
    ...fontStyle.demiBold,
  },
  onDraggingStyle: {
    cursor: "grabbing",
  },
};

const styles = {
  popoverContainerStyle: {
    zIndex: 1100,
  },
  rowPopoverContainerStyle: {
    top: "-10px",
    left: "-10px",
  },
  columnPopoverContainerStyle: {
    top: "-10px",
    left: "-5px",
  },
  rotateIconStyle: {
    transform: "rotate(90deg)",
  },
  scaleIconStyle: {
    transform: "scaleX(-1)",
  },
  transformIconStyle: {
    transform: "rotate(90deg) scaleX(-1)",
  },
  popoverIconStyle: {
    transform: "rotate(90deg)",
    width: "20px",
    height: "16px",
  },
};

const mergedStyles = {
  textAreaStyleColumnCell: {
    ...textAreaStyles.cell,
    ...textAreaStyles.onDraggingStyle,
  },
  textAreaStyleHeaderColumn: {
    ...textAreaStyles.cell,
    ...textAreaStyles.headerCell,
  },
  textAreaStyleHeaderColumnDragging: {
    ...textAreaStyles.cell,
    ...textAreaStyles.headerCell,
    ...textAreaStyles.onDraggingStyle,
  },
  rowPopoverStyles: {
    ...styles.popoverContainerStyle,
    ...styles.rowPopoverContainerStyle,
  },
  columnPopoverStyles: {
    ...styles.popoverContainerStyle,
    ...styles.columnPopoverContainerStyle,
  },
};

const DraggableColumn = ({
  id,
  children,
  selectedColumnOrRow,
  projectedLineData,
  criterias,
}) => {
  const disableDragging = _.some(criterias, criteria =>
    _.includes(_.get(criteria, "id", null), "NEW")
  );
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    node,
  } = useDraggable({ id, disabled: disableDragging });
  let style = {
    transform: !_.isEmpty(transform)
      ? `translate3d(${transform.x}px, ${transform.y}px, 0px)`
      : "",
    transition,
    zIndex: isDragging ? 100 : 1,
    cursor: isDragging ? "grabbing" : "",
    opacity: isDragging ? 0.75 : "",
  };
  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id,
    disabled: disableDragging,
  });
  const ColumnCellClass = classNames({
    [classes.cell]: true,
    [classes.rowHeaderCell]: true,
    [classes.selectedHeaderColumnCell]: selectedColumnOrRow.id === id,
  });

  if (projectedLineData?.id === id) {
    style = { ...style, ...projectedLineData.style };
  }

  return (
    <div
      ref={ref => {
        setNodeRef(ref);
        setDroppableNodeRef(ref);
      }}
      className={ColumnCellClass}
      style={style}
    >
      {cloneElement(children, { node, attributes, listeners, disableDragging })}
    </div>
  );
};

const DraggableRow = ({
  id,
  children,
  selectedColumnOrRow,
  shouldTakeFullWidth,
  projectedLineData,
  indicators,
}) => {
  const disableDragging = _.some(indicators, indicator =>
    _.includes(_.get(indicator, "id", null), "NEW")
  );
  const props = useDraggable({ id, disabled: disableDragging });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    node,
  } = props;

  let style = {
    transform: !_.isEmpty(transform)
      ? `translate3d(${transform.x}px, ${transform.y}px, 0px)`
      : "",
    transition,
    zIndex: isDragging ? 100 : 1,
    cursor: isDragging ? "grabbing !important" : "",
    opacity: isDragging ? 0.75 : "",
  };

  if (projectedLineData?.id === id) {
    style = { ...style, ...projectedLineData.style };
  }

  const disabled = _.includes(id, "NEW");

  const firstColumnCellClass = classNames({
    [classes.firstColumnCell]: true,
    [classes.disabled]: disabled,
    [classes.responsiveCell]: shouldTakeFullWidth,
    [classes.firstIndicatorCell]: selectedColumnOrRow.id === id,
  });

  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id,
    disabled: disableDragging,
  });

  return (
    <div
      ref={ref => {
        setNodeRef(ref);
        setDroppableNodeRef(ref);
      }}
      className={firstColumnCellClass}
      style={style}
    >
      {cloneElement(children, { node, attributes, listeners, disableDragging })}
    </div>
  );
};

const DraggableDescriptor = ({
  id,
  children,
  selectedColumnOrRow,
  descriptor,
  indicators,
  criterias,
  shouldTakeFullWidth,
  showHoverState,
  projectedLineData,
}) => {
  const {
    attributes,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useDraggable({ id });
  let style = {
    transform: !_.isEmpty(transform)
      ? `translate3d(${transform.x}px, ${transform.y}px, 0px)`
      : "",
    transition,
    zIndex: isDragging ? 100 : 1,
    cursor: isDragging ? "grabbing !important" : "",
    opacity: isDragging ? 0.75 : "",
  };
  const disabled = _.includes(descriptor?.id, "NEW");

  const cellClass = classNames(
    { [classes.cell]: true },
    {
      [classes.selectedDescriptorColCell]:
        descriptor?.criteria?.id === selectedColumnOrRow?.id &&
        selectedColumnOrRow?.selectedType === "column",
    },
    {
      [classes.selectedDescriptorRowCell]:
        descriptor?.indicator?.id === selectedColumnOrRow?.id &&
        selectedColumnOrRow?.selectedType === "row",
    },
    {
      [classes.lastDescriptorColCell]:
        descriptor?.criteria?.id === selectedColumnOrRow?.id &&
        selectedColumnOrRow?.selectedType === "column" &&
        descriptor?.indicator.id === _.last(indicators).id,
    },
    {
      [classes.lastDescriptorRowCell]:
        descriptor?.indicator?.id === selectedColumnOrRow?.id &&
        selectedColumnOrRow?.selectedType === "row" &&
        descriptor?.criteria.id === _.last(criterias).id,
    },
    {
      [classes.responsiveCell]: shouldTakeFullWidth,
    },
    {
      [classes.disabled]: disabled,
    },
    {
      [classes.cursorNotAllowed]: disabled,
    },
    {
      [classes.cellHover]: showHoverState && !isDragging,
    },
    {
      [classes.draggingCell]: isDragging,
    }
  );

  if (projectedLineData?.id === id) {
    style = { ...style, ...projectedLineData.style };
  }

  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id,
  });

  return (
    <div
      className={cellClass}
      ref={ref => {
        setNodeRef(ref);
        setDroppableNodeRef(ref);
      }}
      style={style}
    >
      {children}
    </div>
  );
};

const DeleteModal = ({ toggleDeleteModal, onConfirmDelete, selectedType }) => {
  const { t } = useI18n();

  return (
    <Dialog
      variant="delete-dialog"
      title={t("common:delete_with_label", {
        label: t(`common:${selectedType}`),
      })}
      message={t("common:delete_msg_with_label", {
        label: t(`common:${selectedType}`),
      })}
      buttonOneText={t("common:cancel")}
      buttonTwoText={t("common:delete")}
      onButtonOneClick={toggleDeleteModal}
      onButtonTwoClick={onConfirmDelete}
      isClosable={true}
      onClose={toggleDeleteModal}
    />
  );
};

const ColumnCell = memo(props => {
  const {
    setFocusedColumn,
    updateInputRef,
    setFocusedCell,
    writeRubricElement,
    mode,
    criteriaObjId,
    t,
    minRows,
    writeRubricElementLocal,
    saveRubricUpdateActivityStatus,
    setHoverDescriptorDetails,
    descriptors,
    indicatorId,
    isDragging,
    setSelectedColumnOrRow,
  } = props;

  const descriptor = _.find(
    descriptors,
    des =>
      _.get(des, "indicator.id") == indicatorId &&
      _.get(des, "criteria.id") == criteriaObjId
  );

  const [descriptorLabel, setDescriptorLabel] = useState(
    descriptor?.label || ""
  );

  const disabled = _.includes(descriptor?.id, "NEW");

  const refKey = `Descriptor_${descriptor?.id}`;

  const updateInputField = value => {
    setDescriptorLabel(value);
    writeRubricElement({
      elementDetails: descriptor,
      id: _.get(descriptor, "id"),
      label: value,
      type: "DESCRIPTOR",
    });
    saveRubricUpdateActivityStatus();
  };

  return (
    <div
      onFocus={() => {
        setFocusedColumn(criteriaObjId);
      }}
      key={descriptor?.id}
      onMouseEnter={() => {
        setHoverDescriptorDetails({
          indicatorId: descriptor?.indicator?.id,
          criteriaId: descriptor?.criteria?.id,
        });
      }}
      onMouseLeave={() => setHoverDescriptorDetails({})}
      className={classes.textAreaContainer}
    >
      <TextAreaInput
        value={descriptorLabel}
        textAreaStyles={
          !isDragging
            ? textAreaStyles.cell
            : mergedStyles.textAreaStyleColumnCell
        }
        updateInputRef={ref => updateInputRef({ ref, refKey })}
        onBlurInputField={() => {
          if (!isDragging) setFocusedCell(null);
          writeRubricElementLocal({
            elementDetails: descriptor,
            id: _.get(descriptor, "id"),
            label: descriptorLabel,
            type: "DESCRIPTOR",
          });
        }}
        onFocusInputField={() => {
          setFocusedCell(refKey);
          if (!isDragging) setSelectedColumnOrRow({});
        }}
        emptyText={""}
        disabled={disabled}
        mode={mode}
        placeholder={t("common:definition")}
        updateInputField={updateInputField}
        className={classes.placeholderColor}
        minRows={minRows}
      />
    </div>
  );
});

ColumnCell.displayName = "ColumnCell";

const IndicatorCell = memo(props => {
  const {
    t,
    indicatorObj,
    mode,
    indicators,
    minRows,
    updateInputRef,
    setFocusedCell,
    writeRubricElement,
    deleteRubricIndicator,
    writeRubricElementLocal,
    saveRubricUpdateActivityStatus,
    selectedColumnOrRow,
    setSelectedColumnOrRow,
    hoverDescriptorDetails,
    setHoverDescriptorDetails,
    createRubricIndicator,
    isDragging,
    node,
    attributes,
    listeners,
    index,
    indexToFocusData,
    setIndexToFocusData,
    disableDragging,
  } = props;

  const [indicatorLabel, setIndicatorLabel] = useState(
    indicatorObj?.label || ""
  );
  const textAreaRef = useRef(null);
  const rowActionsRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const toggleDeleteModal = () => setShowDeleteModal(prev => !prev);
  const onConfirmDelete = () => {
    deleteRubricIndicator(indicatorObjId);
    setSelectedColumnOrRow({});
    toggleDeleteModal();
    if (index > 0) setIndexToFocusData({ type: "row", index: index - 1 });
    else setIndexToFocusData({ type: "row", index });
  };
  const isFullyVisible = useIntersectionObserver(node, {
    threshold: 0.5,
  });
  // check if the selected item is in the view or not
  useEffect(() => {
    if (
      !isFullyVisible &&
      !isDragging &&
      selectedColumnOrRow.selectedType === "row" &&
      selectedColumnOrRow.id === indicatorObjId
    ) {
      setSelectedColumnOrRow({});
    }
  }, [isFullyVisible]);

  const indicatorObjId = _.get(indicatorObj, "id");
  const refKey = `Indicator_${indicatorObjId}`;
  const disabled = _.includes(indicatorObjId, "NEW");

  const updateInputField = value => {
    setIndicatorLabel(value);
    writeRubricElement({
      elementDetails: indicatorObj,
      id: indicatorObjId,
      label: value,
      type: "INDICATOR",
    });
  };

  const onAddIndicatorBefore = async () => {
    const { before } = getItemBeforeAndAfter(indicators, indicatorObjId);
    setSelectedColumnOrRow({});
    await createRubricIndicator({ putAfterItemId: before?.id || null });
    setIndexToFocusData({ type: "row", index });
  };

  const onAddIndicatorAfter = async () => {
    setSelectedColumnOrRow({});
    await createRubricIndicator({ putAfterItemId: indicatorObjId });
    setIndexToFocusData({ type: "row", index: index + 1 });
  };

  const rowActionsClass = classNames({
    [classes.rowActions]: true,
    [classes.activeColumnActions]: selectedColumnOrRow.id === indicatorObjId,
  });

  const enableRowActionPopover =
    (indicatorObjId === hoverDescriptorDetails?.indicatorId ||
      indicatorObjId === selectedColumnOrRow.id) &&
    !isDragging &&
    node.current &&
    isFullyVisible;

  const enableRowActionPopoverOption =
    indicatorObjId === selectedColumnOrRow.id &&
    rowActionsRef.current &&
    !isDragging &&
    isFullyVisible;

  if (indexToFocusData.index === index && indexToFocusData.type === "row") {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }
  return (
    <div
      onMouseEnter={() =>
        setHoverDescriptorDetails({ indicatorId: indicatorObjId })
      }
      onMouseLeave={() => setHoverDescriptorDetails({})}
      className={classes.textAreaContainer}
    >
      <TextAreaInput
        value={indicatorLabel}
        mode={mode}
        ref={textAreaRef}
        updateInputRef={ref => updateInputRef({ ref, refKey })}
        onBlurInputField={() => {
          setFocusedCell(null);
          if (!_.isEmpty(indexToFocusData)) setIndexToFocusData({});
          writeRubricElementLocal({
            elementDetails: indicatorObj,
            id: indicatorObjId,
            label: indicatorLabel,
            type: "INDICATOR",
          });
          saveRubricUpdateActivityStatus();
        }}
        onFocusInputField={() => {
          setFocusedCell(refKey);
          if (!isDragging) setSelectedColumnOrRow({});
        }}
        textAreaStyles={
          !isDragging
            ? textAreaStyles.cell
            : mergedStyles.textAreaStyleColumnCell
        }
        disabled={disabled}
        emptyText={""}
        placeholder={t("common:description")}
        updateInputField={updateInputField}
        className={classes.placeholderColor}
        minRows={minRows}
      />
      {enableRowActionPopover ? (
        <Popover
          isOpen={true}
          align={"center"}
          positions={["left"]}
          containerStyle={mergedStyles.rowPopoverStyles}
          boundaryInset={20}
          reposition={true}
          content={() => {
            return (
              <div {...listeners} {...attributes}>
                <div
                  id={"RUBRIC_INDICATOR"}
                  className={rowActionsClass}
                  ref={rowActionsRef}
                  onClick={e => {
                    if (selectedColumnOrRow.id === indicatorObjId) {
                      setSelectedColumnOrRow({});
                    } else {
                      setSelectedColumnOrRow({
                        selectedType: "row",
                        id: indicatorObjId,
                      });
                    }
                  }}
                >
                  <DotsSixVerticalOutlined
                    size={"xx-small"}
                    style={styles.popoverIconStyle}
                  />
                </div>
              </div>
            );
          }}
          childrenRef={{ current: node.current }}
          isChildrenRefPassed={true}
        />
      ) : null}

      {enableRowActionPopoverOption ? (
        <Popover
          isOpen={true}
          align={"center"}
          boundaryInset={50}
          positions={["right"]}
          containerStyle={styles.popoverContainerStyle}
          reposition={true}
          onClickOutside={() => setSelectedColumnOrRow({})}
          content={() => {
            return (
              <div
                className={classes.popoverRowActions}
                style={elevations["elevation-2B"]}
              >
                <IconButton
                  dsVersion="2.0"
                  icon={<AddColumnOutlined variant="subtle" />}
                  size={"small"}
                  style={styles.transformIconStyle}
                  variant="neutral"
                  type="plain"
                  onClick={onAddIndicatorBefore}
                  disabled={disabled || disableDragging}
                />
                <IconButton
                  dsVersion="2.0"
                  icon={<AddColumnOutlined variant="subtle" />}
                  style={styles.rotateIconStyle}
                  size={"small"}
                  variant="neutral"
                  type="plain"
                  onClick={onAddIndicatorAfter}
                  disabled={disabled || disableDragging}
                />

                {_.size(indicators) > 1 ? (
                  <>
                    <div className={classes.popoverActionsSeparator} />

                    <IconButton
                      dsVersion="2.0"
                      icon={<DeleteOutlined variant="critical" />}
                      size={"small"}
                      variant="neutral"
                      type="plain"
                      onClick={toggleDeleteModal}
                      disabled={disabled || disableDragging}
                    />
                  </>
                ) : null}
              </div>
            );
          }}
          childrenRef={{
            current: rowActionsRef.current,
          }}
          isChildrenRefPassed={true}
        />
      ) : null}
      {showDeleteModal ? (
        <DeleteModal
          toggleDeleteModal={toggleDeleteModal}
          onConfirmDelete={onConfirmDelete}
          selectedType={"row"}
        />
      ) : null}
    </div>
  );
});

IndicatorCell.displayName = "IndicatorCell";

const HeaderColumns = memo(props => {
  const {
    t,
    mode,
    rubric,
    shouldTakeFullWidth,
    setFocusedColumn,
    updateInputRef,
    setFocusedCell,
    criteriaObj,
    writeRubricElement,
    deleteRubricCriteria,
    writeRubricElementLocal,
    saveRubricUpdateActivityStatus,
    selectedColumnOrRow,
    setSelectedColumnOrRow,
    hoverDescriptorDetails,
    setHoverDescriptorDetails,
    createRubricCriteria,
    isDragging,
    node,
    attributes,
    listeners,
    index,
    indexToFocusData,
    setIndexToFocusData,
    disableDragging,
  } = props;

  const [criteriaLabel, setCriteriaLabel] = useState(criteriaObj?.label || "");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const toggleDeleteModal = () => setShowDeleteModal(prev => !prev);
  const onConfirmDelete = () => {
    setSelectedColumnOrRow({});
    deleteRubricCriteria(criteriaObjId);
    toggleDeleteModal();
    if (index > 0) setIndexToFocusData({ type: "column", index: index - 1 });
    else setIndexToFocusData({ type: "column", index });
  };
  const columnActionsRef = useRef(null);
  const textAreaRef = useRef(null);
  const isFullyVisible = useIntersectionObserver(node, {
    threshold: 0.8,
  });
  // check if the selected item is in the view or not
  useEffect(() => {
    if (
      !isFullyVisible &&
      !isDragging &&
      selectedColumnOrRow.selectedType === "column" &&
      selectedColumnOrRow.id === criteriaObjId
    ) {
      setSelectedColumnOrRow({});
    }
  }, [isFullyVisible]);

  const criterias = _.get(rubric, "criterias", []);
  const criteriaObjId = _.get(criteriaObj, "id");
  const refKey = `Criteria_${criteriaObjId}`;
  const disabled = _.includes(criteriaObjId, "NEW");

  const updateInputField = value => {
    setCriteriaLabel(value);
    writeRubricElement({
      elementDetails: criteriaObj,
      id: criteriaObjId,
      label: value,
      type: "CRITERIA",
    });
  };

  const onAddCriteriaBefore = async (criterias, criteriaObjId) => {
    const { before } = getItemBeforeAndAfter(criterias, criteriaObjId);
    setSelectedColumnOrRow({});
    await createRubricCriteria({ putAfterItemId: before?.id || null });
    setIndexToFocusData({ type: "column", index });
  };

  const onAddCriteriaAfter = async () => {
    setSelectedColumnOrRow({});
    await createRubricCriteria({ putAfterItemId: criteriaObjId });
    setIndexToFocusData({ type: "column", index: index + 1 });
  };

  const rowHeaderCellClass = classNames({
    [classes.responsiveCell]: shouldTakeFullWidth,
    [classes.disabled]: disabled,
    [classes.roundedCell]: selectedColumnOrRow.id === criteriaObjId,
  });

  const pseudoColumnClass = classNames({
    [classes.responsiveCell]: shouldTakeFullWidth,
  });

  const columnActionsClass = classNames({
    [classes.columnActions]: true,
    [classes.activeColumnActions]: selectedColumnOrRow.id === criteriaObjId,
    [classes.cursorStyle]:
      selectedColumnOrRow.id === criteriaObjId && isDragging,
  });

  const enableColumnActionPopover =
    (criteriaObjId === hoverDescriptorDetails?.criteriaId ||
      criteriaObjId === selectedColumnOrRow.id) &&
    !isDragging &&
    isFullyVisible;

  const enableColumnActionPopoverOption =
    criteriaObjId === selectedColumnOrRow.id &&
    columnActionsRef.current &&
    !isDragging &&
    isFullyVisible;

  if (indexToFocusData.index === index && indexToFocusData.type === "column") {
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }

  return (
    <div
      className={pseudoColumnClass}
      onFocus={() => setFocusedColumn(criteriaObjId)}
      key={criteriaObjId}
      onMouseEnter={() =>
        setHoverDescriptorDetails(
          isFullyVisible ? { criteriaId: criteriaObjId } : {}
        )
      }
      onMouseLeave={() => setHoverDescriptorDetails({})}
    >
      <div className={rowHeaderCellClass}>
        <TextAreaInput
          ref={textAreaRef}
          updateInputRef={ref => updateInputRef({ ref, refKey })}
          onBlurInputField={() => {
            setFocusedCell(null);
            if (!_.isEmpty(indexToFocusData)) setIndexToFocusData({});
            writeRubricElementLocal({
              elementDetails: criteriaObj,
              id: criteriaObjId,
              label: criteriaLabel,
              type: "CRITERIA",
            });
            saveRubricUpdateActivityStatus();
          }}
          onFocusInputField={() => {
            setFocusedCell(refKey);
            if (!isDragging) setSelectedColumnOrRow({});
          }}
          value={criteriaLabel}
          mode={mode}
          textAreaStyles={
            !isDragging
              ? mergedStyles.textAreaStyleHeaderColumn
              : mergedStyles.textAreaStyleHeaderColumnDragging
          }
          disabled={disabled}
          minRows={1}
          emptyText={""}
          placeholder={t("common:click_to_add_withLabel", {
            label: t("common:level"),
          })}
          updateInputField={updateInputField}
          className={classes.placeholderTextOnSubtle}
        />

        {enableColumnActionPopover ? (
          <Popover
            isOpen={true}
            align={"center"}
            positions={["top"]}
            containerStyle={mergedStyles.columnPopoverStyles}
            boundaryInset={20}
            reposition={true}
            content={() => {
              return (
                <div {...listeners} {...attributes}>
                  <div
                    id={"RUBRIC_CRITERIA"}
                    className={columnActionsClass}
                    ref={columnActionsRef}
                    onClick={e => {
                      setSelectedColumnOrRow(
                        selectedColumnOrRow.id === criteriaObjId
                          ? {}
                          : {
                              selectedType: "column",
                              id: criteriaObjId,
                            }
                      );
                    }}
                  >
                    <DotsSixVerticalOutlined
                      size={"xx-small"}
                      style={styles.popoverIconStyle}
                    />
                  </div>
                </div>
              );
            }}
            childrenRef={{ current: node.current }}
            isChildrenRefPassed={true}
          />
        ) : null}

        {enableColumnActionPopoverOption ? (
          <Popover
            isOpen={true}
            align={"center"}
            boundaryInset={50}
            positions={["bottom"]}
            containerStyle={styles.popoverContainerStyle}
            reposition={true}
            onClickOutside={() => setSelectedColumnOrRow({})}
            content={() => {
              return (
                <div
                  className={classes.popoverActions}
                  style={elevations["elevation-2B"]}
                >
                  <IconButton
                    dsVersion="2.0"
                    icon={<AddColumnOutlined variant="subtle" />}
                    size={"small"}
                    variant="neutral"
                    type="plain"
                    style={styles.scaleIconStyle}
                    onClick={() =>
                      onAddCriteriaBefore(criterias, criteriaObjId)
                    }
                    disabled={disabled || disableDragging}
                  />

                  <IconButton
                    dsVersion="2.0"
                    icon={<AddColumnOutlined variant="subtle" />}
                    size={"small"}
                    variant="neutral"
                    type="plain"
                    onClick={onAddCriteriaAfter}
                    disabled={disabled || disableDragging}
                  />

                  {_.size(criterias) > 1 ? (
                    <>
                      {" "}
                      <div className={classes.popoverActionsSeparator} />
                      <IconButton
                        dsVersion="2.0"
                        icon={<DeleteOutlined variant="critical" />}
                        size={"small"}
                        variant="neutral"
                        onClick={toggleDeleteModal}
                        disabled={disabled || disableDragging}
                      />
                    </>
                  ) : null}
                </div>
              );
            }}
            childrenRef={{
              current: columnActionsRef.current,
            }}
            isChildrenRefPassed={true}
          />
        ) : null}
        {showDeleteModal ? (
          <DeleteModal
            toggleDeleteModal={toggleDeleteModal}
            onConfirmDelete={onConfirmDelete}
            selectedType={"column"}
          />
        ) : null}
      </div>
    </div>
  );
});

HeaderColumns.displayName = "HeaderColumns";

class RubricTable extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      inFocusColumn: null,
      inFocusRow: null,
    };

    if (props.customRef) {
      props.customRef(this);
    }
    this.focusedCell = null;
    this.cellRefs = {};
    this._isCreatingIndicator = false;
    this._isCreatingCriteria = false;

    this.debouncedWriteRubricElementLocal = _.debounce(
      props.writeRubricElementLocal,
      1000
    );
  }

  blur = () => {
    if (this.focusedCell && this.cellRefs[this.focusedCell]) {
      this.cellRefs[this.focusedCell].blur();
    }
  };

  writeRubricElement = ({ id, type, label, elementDetails }) => {
    const { portalType } = this.props;
    this.debouncedWriteRubricElementLocal({ id, type, label, elementDetails });
    this.props.updateField({
      key: id,
      params: { id, type, label, portalType },
      type: "RUBRIC_ELEMENT",
    });
  };

  saveRubricUpdateActivityStatus = () => {
    const { assignmentId, saveAssessmentToolUpdateStatusInRedux } = this.props;
    saveAssessmentToolUpdateStatusInRedux({ assignmentId, toolType: "RUBRIC" });
  };

  createRubricIndicator = async params => {
    if (this._isCreatingIndicator) return;
    const { putAfterItemId } = params || {};
    if (_.startsWith(putAfterItemId, "NEW")) return;

    const {
      rubric: { id: rubricId },
      createRubricIndicator,
      setScrollToBottom,
      portalType,
    } = this.props;
    this._isCreatingIndicator = true;
    try {
      await createRubricIndicator({ rubricId, portalType, putAfterItemId });
      if (setScrollToBottom) {
        setTimeout(setScrollToBottom);
      }
    } finally {
      this._isCreatingIndicator = false;
    }
  };

  createRubricCriteria = async params => {
    if (this._isCreatingCriteria) return;
    const { putAfterItemId } = params || {};
    if (_.startsWith(putAfterItemId, "NEW")) return;

    const {
      rubric: { id: rubricId },
      createRubricCriteria,
      portalType,
    } = this.props;
    this._isCreatingCriteria = true;
    try {
      await createRubricCriteria({ rubricId, portalType, putAfterItemId });
    } finally {
      this._isCreatingCriteria = false;
    }
  };

  deleteRubricCriteria = id => {
    const {
      rubric: { id: rubricId },
      deleteRubricCriteria,
      portalType,
    } = this.props;
    deleteRubricCriteria({ id, rubricId, portalType });
  };

  deleteRubricIndicator = id => {
    const {
      rubric: { id: rubricId },
      deleteRubricIndicator,
      portalType,
    } = this.props;

    deleteRubricIndicator({ id, rubricId, portalType });
  };

  getHeaderRow = () => {
    const { shouldTakeFullWidth, firstColumnHeaderText = "" } = this.props;

    const pseudoFirstColumnCellClass = classNames({
      [classes.pseudoFirstColumn]: true,
      [classes.responsiveCell]: shouldTakeFullWidth,
    });

    const emptyFirstColumnCellClass = classNames({
      [classes.emptyFirstColumnCell]: true,
      [classes.responsiveCell]: shouldTakeFullWidth,
    });

    return (
      <div className={classes.pseudoRow}>
        <div className={classes.headerRow}>
          <div className={pseudoFirstColumnCellClass}>
            <div className={emptyFirstColumnCellClass}>
              {firstColumnHeaderText}
            </div>
          </div>
          {this.getHeaderColumns()}
        </div>
      </div>
    );
  };

  getHeaderColumns = () => {
    const {
      mode,
      rubric,
      shouldTakeFullWidth,
      t,
      writeRubricElementLocal,
      selectedColumnOrRow,
      setSelectedColumnOrRow,
      hoverDescriptorDetails,
      setHoverDescriptorDetails,
      isDragging,
      indexToFocusData,
      setIndexToFocusData,
    } = this.props;
    const criterias = _.get(rubric, "criterias", []);

    return (
      <SortableContext items={criterias} strategy={rectSortingStrategy}>
        {_.map(criterias, (criteriaObj, index) => {
          return (
            <DraggableColumn
              key={criteriaObj?.id}
              id={criteriaObj?.id}
              {...this.props}
              criterias={criterias}
            >
              <HeaderColumns
                key={criteriaObj?.id}
                t={t}
                criteriaObj={criteriaObj}
                mode={mode}
                rubric={rubric}
                shouldTakeFullWidth={shouldTakeFullWidth}
                setFocusedColumn={this.setFocusedColumn}
                updateInputRef={this.updateInputRef}
                setFocusedCell={this.setFocusedCell}
                writeRubricElement={this.writeRubricElement}
                deleteRubricCriteria={this.deleteRubricCriteria}
                writeRubricElementLocal={writeRubricElementLocal}
                selectedColumnOrRow={selectedColumnOrRow}
                setSelectedColumnOrRow={setSelectedColumnOrRow}
                hoverDescriptorDetails={hoverDescriptorDetails}
                setHoverDescriptorDetails={setHoverDescriptorDetails}
                createRubricCriteria={this.createRubricCriteria}
                isDragging={isDragging}
                setStateFocusedCell={this.setStateFocusedCell}
                saveRubricUpdateActivityStatus={
                  this.saveRubricUpdateActivityStatus
                }
                index={index}
                indexToFocusData={indexToFocusData}
                setIndexToFocusData={setIndexToFocusData}
              />
            </DraggableColumn>
          );
        })}
      </SortableContext>
    );
  };
  setStateFocusedCell = id => this.setState({ focusedCell: id });

  getRows = () => {
    const {
      writeRubricElementLocal,
      setSelectedColumnOrRow,
      selectedColumnOrRow,
      hoverDescriptorDetails,
      setHoverDescriptorDetails,
    } = this.props;

    const indicators = _.get(this.props.rubric, "indicators", []);
    const criterias = _.get(this.props.rubric, "criterias", []);
    const descriptors = _.get(this.props.rubric, "descriptors", []);

    return (
      <SortableContext items={indicators} strategy={rectSortingStrategy}>
        {_.map(indicators, (indicatorObj, index) => {
          const indicatorObjId = _.get(indicatorObj, "id");
          return (
            <div
              className={classes.pseudoRow}
              key={indicatorObjId}
              onFocus={() => {
                this.setState({
                  inFocusRow: indicatorObjId,
                  focusedCell: indicatorObjId,
                });
              }}
            >
              <div className={classes.row}>
                <DraggableRow
                  id={indicatorObj?.id}
                  indicators={indicators}
                  {...this.props}
                >
                  <IndicatorCell
                    {...this.props}
                    indicators={indicators}
                    indicatorObj={indicatorObj}
                    updateInputRef={this.updateInputRef}
                    setFocusedCell={this.setFocusedCell}
                    writeRubricElement={this.writeRubricElement}
                    deleteRubricIndicator={this.deleteRubricIndicator}
                    writeRubricElementLocal={writeRubricElementLocal}
                    selectedColumnOrRow={selectedColumnOrRow}
                    setSelectedColumnOrRow={setSelectedColumnOrRow}
                    hoverDescriptorDetails={hoverDescriptorDetails}
                    setHoverDescriptorDetails={setHoverDescriptorDetails}
                    createRubricIndicator={this.createRubricIndicator}
                    saveRubricUpdateActivityStatus={
                      this.saveRubricUpdateActivityStatus
                    }
                    setStateFocusedCell={this.setStateFocusedCell}
                    index={index}
                  />
                </DraggableRow>

                {this.getColumns({
                  indicatorId: indicatorObjId,
                  criterias,
                  descriptors,
                })}
              </div>
            </div>
          );
        })}
      </SortableContext>
    );
  };

  setFocusedColumn = columnId => {
    this.setState({ inFocusColumn: columnId, focusedCell: columnId });
  };

  updateInputRef = ({ ref, refKey }) => {
    this.cellRefs[refKey] = ref;
  };

  setFocusedCell = refKey => {
    this.focusedCell = refKey;
  };

  getColumns = params => {
    const { indicatorId, criterias, descriptors } = params;

    const {
      mode,
      shouldTakeFullWidth = false,
      t,
      minRows,
      showHoverState,
      writeRubricElementLocal,
      hoverDescriptorDetails,
      selectedColumnOrRow,
      setHoverDescriptorDetails,
      isDragging,
      setSelectedColumnOrRow,
    } = this.props;

    const indicators = _.get(this.props.rubric, "indicators", []);

    return _.map(criterias, criteriaObj => {
      const descriptor = _.find(
        descriptors,
        des =>
          _.get(des, "indicator.id") == indicatorId &&
          _.get(des, "criteria.id") == _.get(criteriaObj, "id")
      );
      return (
        <DraggableDescriptor
          key={`${descriptor?.indicator?.id}-${descriptor?.criteria?.id}`}
          id={
            _.some(criterias, { id: this.state.focusedCell })
              ? descriptor?.criteria?.id
              : descriptor?.indicator?.id
          }
          descriptor={descriptor}
          indicators={indicators}
          criterias={criterias}
          {...this.props}
        >
          <ColumnCell
            t={t}
            key={criteriaObj?.id}
            criteriaObjId={criteriaObj?.id}
            setFocusedColumn={this.setFocusedColumn}
            updateInputRef={this.updateInputRef}
            setFocusedCell={this.setFocusedCell}
            writeRubricElement={this.writeRubricElement}
            mode={mode}
            shouldTakeFullWidth={shouldTakeFullWidth}
            minRows={minRows}
            showHoverState={showHoverState}
            writeRubricElementLocal={writeRubricElementLocal}
            saveRubricUpdateActivityStatus={this.saveRubricUpdateActivityStatus}
            hoverDescriptorDetails={hoverDescriptorDetails}
            setHoverDescriptorDetails={setHoverDescriptorDetails}
            descriptors={descriptors}
            indicatorId={indicatorId}
            selectedColumnOrRow={selectedColumnOrRow}
            indicators={indicators}
            criterias={criterias}
            isDragging={isDragging}
            setSelectedColumnOrRow={setSelectedColumnOrRow}
          />
        </DraggableDescriptor>
      );
    });
  };

  renderActionButtons = () => {
    const {
      t,
      mode,
      showActionButtons = true,
      rubric,
      onClickToolSave,
      showSaveTemplateButton,
      rubricIndicatorButtonProps = {},
      rubricCriteriaButtonProps = {},
      rubricTemplateButtonProps = {},
      actionButtonsUIType = "BUTTON",
    } = this.props;

    if (mode === "view" || !showActionButtons) return null;

    const criterias = _.get(rubric, "criterias", []);
    const indicators = _.get(rubric, "indicators", []);

    const disableButtons =
      _.includes(_.last(indicators)?.id, "NEW") ||
      _.includes(_.last(criterias)?.id, "NEW");

    const renderButtons = () => {
      switch (actionButtonsUIType) {
        case "BUTTON": {
          return (
            <>
              <div className={classes.borderLeftContainer}>
                <Button
                  dsVersion="2.0"
                  size={"medium"}
                  variant="neutral"
                  type="outlined"
                  icon={
                    <AddColumnOutlined
                      size={"xx-small"}
                      style={styles.rotateIconStyle}
                      variant="subtle"
                    />
                  }
                  onClick={this.createRubricIndicator}
                  disabled={disableButtons}
                  {...rubricIndicatorButtonProps}
                >
                  {t("common:add_with_label", { label: t("common:row") })}
                </Button>
                <Button
                  dsVersion="2.0"
                  size={"medium"}
                  variant="neutral"
                  type="outlined"
                  icon={
                    <AddColumnOutlined size={"xx-small"} variant="subtle" />
                  }
                  onClick={this.createRubricCriteria}
                  disabled={disableButtons}
                  {...rubricCriteriaButtonProps}
                >
                  {t("common:add_with_label", {
                    label: t("common:column"),
                  })}
                </Button>
              </div>

              {showSaveTemplateButton ? (
                <Button
                  dsVersion="2.0"
                  variant={"outlined-subtle"}
                  size={"medium"}
                  onClick={onClickToolSave}
                  {...rubricTemplateButtonProps}
                >
                  {t("classRoom:save_as_template")}
                </Button>
              ) : null}
            </>
          );
        }
        case "ICON_BUTTON": {
          return (
            <>
              <div className={classes.iconButtonContainer}>
                <Tooltip
                  placement={"top"}
                  tooltip={t("common:add_with_label", {
                    label: t("common:criteria"),
                  })}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion="2.0"
                    icon={<AddColumnOutlined variant="subtle" />}
                    style={styles.rotateIconStyle}
                    variant="neutral"
                    type="plain"
                    size={"small"}
                    disabled={disableButtons}
                    onClick={this.createRubricIndicator}
                  />
                </Tooltip>
                <div className={classes.iconSeparator} />
                <Tooltip
                  placement={"top"}
                  tooltip={t("common:add_with_label", {
                    label: t("common:levels"),
                  })}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion="2.0"
                    icon={<AddColumnOutlined variant="subtle" />}
                    variant="neutral"
                    type="plain"
                    size={"small"}
                    disabled={disableButtons}
                    onClick={this.createRubricCriteria}
                  />
                </Tooltip>
              </div>

              {showSaveTemplateButton ? (
                <Tooltip
                  placement={"top"}
                  tooltip={t("classRoom:save_as_template")}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion="2.0"
                    icon={<AssessmentApplicableOutlined variant="subtle" />}
                    variant="neutral"
                    type="plain"
                    size={"small"}
                    onClick={onClickToolSave}
                  />
                </Tooltip>
              ) : null}
            </>
          );
        }
      }
    };
    return <div className={classes.bottomContainer}>{renderButtons()}</div>;
  };

  render() {
    const { mode, rubric, conStyle, innerConStyle, t } = this.props;

    const containerClass = classNames(
      { [classes.container]: true },
      { [classes.viewContainer]: mode == "view" }
    );

    return (
      <React.Fragment>
        <div
          onBlur={() => {
            this.setState({ inFocusColumn: null, inFocusRow: null });
          }}
          className={containerClass}
          style={conStyle}
        >
          <div className={classes.innerContainer} style={innerConStyle}>
            {rubric ? (
              this.getHeaderRow()
            ) : (
              <div className={classes.emptyText} style={{ padding: "16px" }}>
                {t("common:no_label_defined", { label: "rubric" })}
              </div>
            )}
            {rubric ? this.getRows() : null}
          </div>
        </div>
        {this.renderActionButtons()}
      </React.Fragment>
    );
  }
}

const RubricTableWithReordering = props => {
  const { rubric, reorderAssessmentToolItem } = props;
  const { criterias, indicators } = rubric || {};

  const [selectedColumnOrRow, setSelectedColumnOrRow] = useState({});
  const [hoverDescriptorDetails, setHoverDescriptorDetails] = useState({
    indicatorId: null,
    criteriaId: null,
  });
  const [projectedLineData, setProjectedLineData] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [indexToFocusData, setIndexToFocusData] = useState({});

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  const onDragStart = event => {
    setIsDragging(true);
    setHoverDescriptorDetails({});
    if (_.some(criterias, { id: event.active.id })) {
      setSelectedColumnOrRow({
        selectedType: "column",
        id: event.active.id,
      });
    } else {
      setSelectedColumnOrRow({
        selectedType: "row",
        id: event.active.id,
      });
    }
  };

  const onDragMove = params => {
    const { active, over, delta } = params;
    const { id: overId, rect: overRect = {} } = over || {};
    const { id: activeId, rect: activeRect = {} } = active || {};
    const { current = {} } = activeRect || {};
    const { translated = {} } = current || {};
    if (
      _.isEmpty(translated) ||
      !translated.left ||
      !translated.right ||
      isEmpty(selectedColumnOrRow)
    ) {
      return;
    }
    const newProjectedLineData = getProjectionLineData({
      overId,
      overRect,
      activeId,
      delta,
      translated,
      dragType: selectedColumnOrRow.selectedType,
      assessmentToolArray:
        selectedColumnOrRow.selectedType === "column" ? criterias : indicators,
    });

    if (!_.isEqual(projectedLineData, newProjectedLineData)) {
      setProjectedLineData(newProjectedLineData);
    }
    return;
  };

  const onDragEnd = async params => {
    // reset all the states
    setIsDragging(false);
    setSelectedColumnOrRow({});
    setHoverDescriptorDetails({
      indicatorId: null,
      criteriaId: null,
    });
    setProjectedLineData({});

    const { active, over } = params;
    const activeId = active.id;
    const overId = _.get(over, "id", null);

    const itemType =
      selectedColumnOrRow.selectedType === "column"
        ? "RUBRIC_CRITERIA"
        : "RUBRIC_INDICATOR";
    const items =
      selectedColumnOrRow.selectedType === "column" ? criterias : indicators;

    const fromIndex = _.findIndex(items, { id: activeId });
    const toIndex = _.findIndex(items, { id: overId });

    if (activeId !== overId) {
      let putAfterItemId = null;
      if (toIndex > 0) {
        if (fromIndex < toIndex) {
          putAfterItemId = _.get(items, `${[toIndex]}.id`, null);
        } else {
          putAfterItemId = _.get(items, `${[toIndex - 1]}.id`, null);
        }
      } else if (toIndex == _.size(items) - 1) {
        putAfterItemId = _.get(items.last(), `id`, null);
      }
      const updatedValue = moveElementInArray({
        array: items,
        fromIndex,
        toIndex,
      });
      await reorderAssessmentToolItem({
        itemId: activeId,
        itemType,
        putAfterItemId,
        toolType: RUBRIC,
        toolId: rubric.id,
        updatedValue,
      });
    }
  };

  return (
    <DndContext
      collisionDetection={closestCorners}
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragMove={onDragMove}
    >
      <RubricTable
        {...props}
        selectedColumnOrRow={selectedColumnOrRow}
        setSelectedColumnOrRow={setSelectedColumnOrRow}
        hoverDescriptorDetails={hoverDescriptorDetails}
        setHoverDescriptorDetails={setHoverDescriptorDetails}
        isDragging={isDragging}
        projectedLineData={projectedLineData}
        setIndexToFocusData={setIndexToFocusData}
        indexToFocusData={indexToFocusData}
      />
    </DndContext>
  );
};

const mapActionCreators = {
  createRubricCriteria,
  createRubricIndicator,
  deleteRubricCriteria,
  deleteRubricIndicator,
  writeRubricElementLocal,
  updateField,
  saveAssessmentToolUpdateStatusInRedux,
  reorderAssessmentToolItem,
};

const mapStateToProps = (state, ownProps) => {
  const { rubric } = ownProps;

  const userType = state.login.userInfo.user_type;
  return {
    userType,
    rubric: sortAssessmentTool({ tool: rubric, type: RUBRIC }),
  };
};

export default compose(
  I18nHOC({ resource: ["unitPlan", "common", "classRoom"] }),
  connect(mapStateToProps, mapActionCreators)
)(RubricTableWithReordering);

RubricTableWithReordering.defaultProps = {
  isEvaluation: false,
  toolEvaluationResponses: [],
  isEvaluatedByStudent: false,
  showInsight: false,
  insights: [],
  showSaveTemplateButton: true,
};
